// Analytical Platform Privacy Policy (EN)
// Generated from the source Word document - keep the wording verbatim.
import type { LegalDocument } from "./types";

const privacyPolicyEn: LegalDocument = {
  "title": "Analytical Platform Privacy Policy",
  "effectiveFrom": "Effective from 18 September 2026",
  "sections": [
    {
      "heading": "1. Introductory Provisions",
      "blocks": [
        {
          "kind": "clause",
          "number": "1.1.",
          "text": "This Privacy Policy (the “Policy”) governs the processing of personal data in connection with the use of the Analytical Platform technology platform, the individual applications, products, tools and functionalities provided through it, and related services (collectively, the “Service”)."
        },
        {
          "kind": "clause",
          "number": "1.2.",
          "text": "Analytical Platform is operated by CCFR, an association of CCF RESEARCH, a.s. and CCFR SOFTWARE, s.r.o. (the “Provider”)."
        },
        {
          "kind": "clause",
          "number": "1.3.",
          "text": "Analytical Platform is a technology environment through which the Provider makes individual applications, products, tools and related functionalities available to Users. Individual applications or products may also be presented on separate websites and may use a shared Analytical Platform user account."
        },
        {
          "kind": "clause",
          "number": "1.4.",
          "text": "The scope of personal data processed may vary depending on which applications, products or functionalities the User uses."
        },
        {
          "kind": "clause",
          "number": "1.5.",
          "text": "The Provider processes personal data in accordance with Regulation (EU) 2016/679 of the European Parliament and of the Council (the “GDPR”) and other applicable laws."
        }
      ]
    },
    {
      "heading": "2. Personal Data We Process",
      "blocks": [
        {
          "kind": "clause",
          "number": "2.1.",
          "text": "Depending on how the User uses the Service, the Provider may process in particular:"
        },
        {
          "kind": "bullets",
          "items": [
            "identification and contact data, including name, surname, email address, telephone number and, where applicable, details of the company on whose behalf the User acts;",
            "user account data, including data required for registration, login, account administration, settings and User preferences;",
            "subscription and payment data, including the selected plan, subscription status, payments and billing information;",
            "Service usage data, including information about applications and functionalities used, User settings, stored outputs, selected parameters and other actions performed through the Service;",
            "portfolio-related data where the User uses an application or functionality enabling work with an investment portfolio;",
            "data relating to technical connectivity with a Broker where the User uses an application or functionality enabling such connectivity;",
            "communication data, including communications with customer or technical support; and",
            "technical and operational data, including IP address, device type, operating system, web browser, date and time of access, operational and diagnostic data and information about use of the Service."
          ]
        },
        {
          "kind": "clause",
          "number": "2.2.",
          "text": "The Provider processes only personal data that is adequate and necessary for the relevant processing purpose."
        },
        {
          "kind": "clause",
          "number": "2.3.",
          "text": "If the User provides the Provider with personal data relating to another person, the User is responsible for ensuring that they are entitled to provide such data."
        }
      ]
    },
    {
      "heading": "3. Purposes and Legal Bases of Processing",
      "blocks": [
        {
          "kind": "clause",
          "number": "3.1.",
          "text": "The Provider processes personal data in particular for:"
        },
        {
          "kind": "bullets",
          "items": [
            "registration and administration of a shared user account;",
            "provision and operation of the Service and individual applications, products and functionalities;",
            "storing settings and data required for individual functionalities;",
            "administration of subscriptions and payments;",
            "technical connectivity with third-party services where activated by the User;",
            "customer and technical support;",
            "security of the Service and prevention of misuse;",
            "diagnostics and resolution of technical issues;",
            "protection of the Provider's rights and legal claims;",
            "compliance with legal obligations;",
            "improvement of the operation and user experience of the Service; and",
            "sending commercial communications where the statutory conditions are met."
          ]
        },
        {
          "kind": "clause",
          "number": "3.2.",
          "text": "Depending on the particular purpose, the legal basis for processing is in particular:"
        },
        {
          "kind": "bullets",
          "items": [
            "provision of the Service and performance of obligations arising from the User's acceptance of the Terms and Conditions, including access to individual applications, products and functionalities within the selected subscription;",
            "compliance with a legal obligation of the Provider;",
            "the Provider's legitimate interests, in particular in securing, protecting and developing the Service, preventing misuse and protecting legal claims; or",
            "the User's consent where required for the particular processing."
          ]
        },
        {
          "kind": "clause",
          "number": "3.3.",
          "text": "Where processing is based on consent, the User may withdraw consent at any time. Withdrawal does not affect the lawfulness of processing carried out before withdrawal."
        }
      ]
    },
    {
      "heading": "4. Data Relating to Individual Applications and Functionalities",
      "blocks": [
        {
          "kind": "clause",
          "number": "4.1.",
          "text": "Individual applications and functionalities available through Analytical Platform may, depending on their purpose, process different categories of data entered, created or made available by the User."
        },
        {
          "kind": "clause",
          "number": "4.2.",
          "text": "Where the User uses portfolio-related functionalities, the Provider may process in particular information about the portfolio, its composition, settings, selected parameters and criteria, and other data required for the relevant functionality."
        },
        {
          "kind": "clause",
          "number": "4.3.",
          "text": "Such data may be processed by automated calculations, algorithms and models used by the Service."
        },
        {
          "kind": "clause",
          "number": "4.4.",
          "text": "The Provider does not process portfolio data and parameters selected by the User for the purpose of individually assessing the User's personal or financial circumstances or the suitability of a particular investment or portfolio for the User."
        },
        {
          "kind": "clause",
          "number": "4.5.",
          "text": "Automated processing carried out within the Service does not in itself constitute decision-making based solely on automated processing that produces legal effects concerning the User or similarly significantly affects the User within the meaning of Article 22 GDPR, unless otherwise stated for a particular functionality."
        }
      ]
    },
    {
      "heading": "5. Connectivity with Brokers and Other Third-Party Services",
      "blocks": [
        {
          "kind": "clause",
          "number": "5.1.",
          "text": "Certain applications or functionalities may enable technical connectivity to an account maintained by the User with an investment services provider (a “Broker”) or another third-party service."
        },
        {
          "kind": "clause",
          "number": "5.2.",
          "text": "If the User activates such connectivity, the Provider may process data necessary to provide it."
        },
        {
          "kind": "clause",
          "number": "5.3.",
          "text": "This may include technical identifiers, authentication or authorisation data, and data required to retrieve information or provide functionalities activated by the User."
        },
        {
          "kind": "clause",
          "number": "5.4.",
          "text": "The Provider processes such data only to the extent necessary to provide the relevant functionality."
        },
        {
          "kind": "clause",
          "number": "5.5.",
          "text": "A Broker or other third-party provider may process personal data as an independent controller when providing its own services, in accordance with its own terms and privacy policy."
        }
      ]
    },
    {
      "heading": "6. Recipients of Personal Data",
      "blocks": [
        {
          "kind": "clause",
          "number": "6.1.",
          "text": "The Provider does not sell personal data to third parties."
        },
        {
          "kind": "clause",
          "number": "6.2.",
          "text": "Personal data may, to the extent necessary, be disclosed to suppliers and other persons whose services the Provider uses to operate and provide the Service. These may include providers of:"
        },
        {
          "kind": "bullets",
          "items": [
            "IT, server and cloud infrastructure;",
            "technical support and development;",
            "email and communication services;",
            "analytical, diagnostic and security tools;",
            "payment services; and",
            "accounting, tax and legal services."
          ]
        },
        {
          "kind": "clause",
          "number": "6.3.",
          "text": "Where such a person acts as a processor, it processes personal data only on the Provider's instructions and under conditions compliant with the GDPR."
        },
        {
          "kind": "clause",
          "number": "6.4.",
          "text": "Personal data may also be disclosed to public authorities or other authorised persons where required by law or by a decision of a competent authority."
        }
      ]
    },
    {
      "heading": "7. Transfers Outside the European Economic Area",
      "blocks": [
        {
          "kind": "clause",
          "number": "7.1.",
          "text": "Some of the Provider's suppliers or their technical infrastructure may be located outside the European Economic Area (EEA)."
        },
        {
          "kind": "clause",
          "number": "7.2.",
          "text": "Where personal data is transferred outside the EEA, the Provider ensures that the transfer is made in accordance with the GDPR and using an appropriate data protection mechanism."
        },
        {
          "kind": "clause",
          "number": "7.3.",
          "text": "Such mechanism may include an adequacy decision of the European Commission, Standard Contractual Clauses approved by the European Commission, or another mechanism permitted by the GDPR."
        }
      ]
    },
    {
      "heading": "8. Retention of Personal Data",
      "blocks": [
        {
          "kind": "clause",
          "number": "8.1.",
          "text": "The Provider retains personal data only for as long as necessary for the purposes for which it is processed or for the period required by law."
        },
        {
          "kind": "clause",
          "number": "8.2.",
          "text": "Data relating to the user account and provision of the Service is generally retained for the duration of the user account or subscription and subsequently for the period necessary to protect the Provider's legal claims."
        },
        {
          "kind": "clause",
          "number": "8.3.",
          "text": "Accounting, tax and related documents are retained for the periods required by applicable law."
        },
        {
          "kind": "clause",
          "number": "8.4.",
          "text": "Data processed on the basis of consent is retained until consent is withdrawn or until expiry of the period for which consent was given, unless another legal basis permits further processing."
        },
        {
          "kind": "clause",
          "number": "8.5.",
          "text": "Technical, operational and security logs may be retained for the period necessary to secure the Service, diagnose issues, prevent misuse and protect legal claims."
        },
        {
          "kind": "clause",
          "number": "8.6.",
          "text": "After the relevant retention period expires, personal data is deleted or anonymised unless another legal basis requires or permits further retention."
        }
      ]
    },
    {
      "heading": "9. Commercial Communications",
      "blocks": [
        {
          "kind": "clause",
          "number": "9.1.",
          "text": "The Provider may send commercial communications to the User where the conditions laid down by applicable law are met."
        },
        {
          "kind": "clause",
          "number": "9.2.",
          "text": "Where commercial communications are based on the User's consent, the User may withdraw that consent at any time."
        },
        {
          "kind": "clause",
          "number": "9.3.",
          "text": "Where the User is a customer of the Provider, the Provider may, subject to applicable law, send information about its own similar products or services."
        },
        {
          "kind": "clause",
          "number": "9.4.",
          "text": "The User may unsubscribe from commercial communications at any time using the link included in the relevant communication or by contacting the Provider."
        },
        {
          "kind": "clause",
          "number": "9.5.",
          "text": "Unsubscribing from commercial communications does not affect operational, service, security or other messages necessary for provision of the Service."
        }
      ]
    },
    {
      "heading": "10. Security of Personal Data",
      "blocks": [
        {
          "kind": "clause",
          "number": "10.1.",
          "text": "The Provider implements appropriate technical and organisational measures to protect personal data against unauthorised access, loss, misuse, alteration, disclosure or destruction."
        },
        {
          "kind": "clause",
          "number": "10.2.",
          "text": "Access to personal data is limited to persons who require it to perform their employment or contractual duties and who are subject to an appropriate duty of confidentiality."
        },
        {
          "kind": "clause",
          "number": "10.3.",
          "text": "Nevertheless, absolute security of any information system or transmission of data over the internet cannot be guaranteed."
        }
      ]
    },
    {
      "heading": "11. User Rights",
      "blocks": [
        {
          "kind": "clause",
          "number": "11.1.",
          "text": "Subject to the conditions set out in the GDPR, the User has in particular:"
        },
        {
          "kind": "bullets",
          "items": [
            "the right to obtain information about the processing of personal data and the right of access;",
            "the right to rectification of inaccurate data or completion of incomplete data;",
            "the right to erasure;",
            "the right to restriction of processing;",
            "the right to data portability;",
            "the right to object to processing based on legitimate interests;",
            "the right to object at any time to processing for direct marketing purposes;",
            "the right to withdraw consent where processing is based on consent; and",
            "the right to lodge a complaint with the competent supervisory authority."
          ]
        },
        {
          "kind": "clause",
          "number": "11.2.",
          "text": "These rights are not absolute and apply subject to the conditions and to the extent provided by the GDPR and other applicable laws."
        },
        {
          "kind": "clause",
          "number": "11.3.",
          "text": "Withdrawal of consent does not affect the lawfulness of processing carried out before withdrawal."
        }
      ]
    },
    {
      "heading": "12. Exercising Rights and Complaints",
      "blocks": [
        {
          "kind": "clause",
          "number": "12.1.",
          "text": "The User may exercise their rights using the Provider's contact details below."
        },
        {
          "kind": "clause",
          "number": "12.2.",
          "text": "Before processing a request, the Provider may reasonably verify the identity of the applicant where necessary to protect personal data."
        },
        {
          "kind": "clause",
          "number": "12.3.",
          "text": "The Provider handles requests within the time limits set by the GDPR."
        },
        {
          "kind": "clause",
          "number": "12.4.",
          "text": "If the User believes that their personal data is being processed contrary to applicable law, the User has the right to lodge a complaint with the competent supervisory authority. In the Czech Republic this is, in particular:"
        },
        {
          "kind": "note",
          "lines": [
            "Office for Personal Data Protection",
            "Pplk. Sochora 27",
            "170 00 Prague 7",
            "Czech Republic"
          ]
        }
      ]
    },
    {
      "heading": "13. Changes to this Policy",
      "blocks": [
        {
          "kind": "clause",
          "number": "13.1.",
          "text": "The Provider may amend this Policy to a reasonable extent, in particular in response to changes to the Service, individual applications or products, technologies used, methods of processing personal data or applicable law."
        },
        {
          "kind": "clause",
          "number": "13.2.",
          "text": "The current version of the Policy is available through Analytical Platform or on the Provider's website."
        },
        {
          "kind": "clause",
          "number": "13.3.",
          "text": "The User may also be informed of material changes in another appropriate manner."
        }
      ]
    },
    {
      "heading": "14. Contact Details",
      "blocks": [
        {
          "kind": "clause",
          "number": "14.1.",
          "text": "For questions concerning personal data protection or to exercise rights under this Policy, the User may contact the Provider:"
        },
        {
          "kind": "note",
          "lines": [
            "CCFR",
            "Email: app@analyticalplatform.com",
            "Phone: +420 530 338 808"
          ]
        }
      ]
    }
  ]
};

export default privacyPolicyEn;
